import { functionsUrl, supabase } from './supabase.ts';
import { ApiError } from './api.ts';

/**
 * The raw client for the `agent-chat` edge function.
 *
 * Same shape as `api.ts` — a bearer token pulled off the live Supabase
 * session, JSON in and out — except `sendMessage`, which POSTs and then reads
 * its response body as a stream rather than waiting for it to finish. That is
 * the one real difference between this file and `api.ts`, and the reason it
 * is not just a few more entries on the `api` object.
 */

export interface ChatSession {
  id: string;
  project_id: string;
  user_id: string;
  title: string;
  status: 'idle' | 'running' | 'error';
  managed_session_id: string | null;
  contextualized: boolean;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface ChatMessageRow {
  id: string;
  session_id: string;
  project_id: string;
  role: 'user' | 'assistant' | 'status' | 'error';
  body: string;
  meta: unknown;
  created_at: string;
}

/** One line of the NDJSON stream `sendMessage` yields. Loosely typed — see agent-chat/index.ts. */
export interface ChatStreamFrame {
  type: 'status' | 'text_delta' | 'text_final' | 'done' | 'error';
  id?: string;
  text?: string;
  activity?: string;
  message?: string;
  ephemeral?: boolean;
}

async function authedHeaders(withBody: boolean): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError(401, 'signed out — sign in again');
  return { authorization: `Bearer ${token}`, ...(withBody ? { 'content-type': 'application/json' } : {}) };
}

async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const response = await fetch(`${functionsUrl}/agent-chat${path}`, {
    method: init.method ?? 'POST',
    headers: await authedHeaders(init.body !== undefined),
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    const body = await response.text();
    if (!response.ok) throw new ApiError(response.status, body.slice(0, 300) || response.statusText);
    return body as T;
  }
  const body = await response.json();
  if (!response.ok) throw new ApiError(response.status, body?.error ?? response.statusText, body?.detail);
  return body as T;
}

export const agentChat = {
  createSession: (projectId: string, title?: string) =>
    call<{ session: ChatSession }>('/sessions', { body: { project_id: projectId, title } }),

  deleteSession: (id: string) => call<{ ok: true }>(`/sessions/${id}`, { method: 'DELETE' }),

  interrupt: (id: string) => call<{ ok: true }>(`/sessions/${id}/interrupt`),

  /** Send a message and stream the reply as parsed NDJSON frames, one per line. */
  async *sendMessage(id: string, text: string, signal?: AbortSignal): AsyncGenerator<ChatStreamFrame> {
    const response = await fetch(`${functionsUrl}/agent-chat/sessions/${id}/messages`, {
      method: 'POST',
      headers: await authedHeaders(true),
      body: JSON.stringify({ text }),
      signal,
    });

    if (!response.ok || !response.body) {
      const errorBody = await response.text().catch(() => '');
      let message = '';
      try {
        message = (JSON.parse(errorBody) as { error?: string }).error ?? '';
      } catch {
        // Not JSON — a gateway failure rather than one of ours.
        message = errorBody.slice(0, 300).trim();
      }
      // Always carry the status. A 502 from the edge gateway can have an
      // empty body and an empty statusText, and "" reads to a person as the
      // request having silently done nothing.
      throw new ApiError(
        response.status,
        message ? `${message} (HTTP ${response.status})` : `HTTP ${response.status} from the assistant`,
      );
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) return;
        buffer += decoder.decode(value, { stream: true });

        let newline: number;
        while ((newline = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newline).trim();
          buffer = buffer.slice(newline + 1);
          if (!line) continue;
          try {
            yield JSON.parse(line) as ChatStreamFrame;
          } catch {
            // A partial or malformed line — skip it rather than breaking the stream.
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },
};
